import React from 'react';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {getBindingPathFieldType} from '@packages/systems/dynamo/utils/BindingRecordUtils';
import {cacheMemo, weakMemo} from '@packages/utilities/memo';
import {generateGuid} from '@packages/utilities/guid';
import {Tree} from '@packages/systems/tree';
import {
  createJsonNode,
  getRichTextValueData,
} from '@packages/systems/core/utils/JsonDomUtils';
import {dataTypesFromComponents} from '@packages/systems/core/render';
import type {RichTextValue} from '@packages/systems/dynamo/binding-props';
import {ExpansionProps} from '../types';
import {EList, Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  Component,
  ExpressionPath,
  Expressions,
  Plugins,
} from '@packages/systems/wfdl';
import {isRichText} from '@packages/utilities/elementTypeUtils';
import {freshEnvForReifiedSubtree} from '../Env';

const memo1000 = cacheMemo(1000);

const generatePlaintextJsonNode = (value: undefined | RichTextValue) => {
  const plaintextNodeId = generateGuid();
  return {
    jsonContentNodes: [
      createJsonNode({
        guid: plaintextNodeId,
        text: true,
        value,
      }),
    ],
    rootChildIds: [plaintextNodeId],
  };
};

// Because plaintext fields can have HTML that gets escaped on render, it could
// overlap with a richText field's value but we want their HTML content to be
// expanded differently, each with their own cache.
// We need to memoize caches for nodes using this function separately to avoid
// performance issues on pages with multiple fields rendered or really bigrich
// rich text nodes.
const booleanMemo = cacheMemo(2);
const getElementChildrenFromValue = booleanMemo((plaintextMode: boolean) =>
  memo1000((value?: RichTextValue) =>
    weakMemo((componentMap: Component.ComponentMap) =>
      weakMemo((plugins: Plugins.PluginsType): Expression => {
        const {jsonContentNodes, rootChildIds} = plaintextMode
          ? generatePlaintextJsonNode(value)
          : getRichTextValueData(
              plugins,
              dataTypesFromComponents(plugins, componentMap),
              value
            );

        const element = Tree.create(componentMap).getElementWithJSONNodes(
          [
            {
              _id: generateGuid(),
              type: 'RichText',
              tag: 'div',
              children: rootChildIds,
            },
            ...jsonContentNodes,
          ],
          plugins
        );

        return (
          Expressions.getIn(Expressions.getElementData(element), [
            {in: 'Record', at: 'children'},
          ]) || EList([])
        );
      })
    )
  )
);

export function replaceRichTextInnerHTMLProp<T extends ExpansionProps>(
  props: T,
  runtimeEnvironment: Environment
): T {
  const {
    data,
    element,
    environmentContext,
    components,
    plugins,
    evalExpr,
    fullEnv,
  } = props;

  const isDesigner = runtimeEnvironment === WebflowEnvironments.DESIGNER;

  // While PlainText or RichText field data in the prop `innerHTML` is a string, ContentEditor needs it to be
  // a node tree. So, we expand the `innerHTML` string into a node tree and add it as children of the current
  // node, while omitting passing the original `innerHTML` down the atom tree. This way the renderer will ignore
  // the original innerHTML binding data and render the expanded innerHTML tree as any other sub-tree.
  const innerHTMLVal: RichTextValue = props.innerHTML as any;

  const expandInnerHtmlForEditorContentEditor =
    innerHTMLVal &&
    typeof innerHTMLVal === 'string' &&
    isDesigner &&
    hasPlainTextOrRichTextInnerHtmlBinding(element) &&
    // While a textHolder is being edited with the ContentEditor, it's actually rendering a 'detached' subtree
    // stored in ContentEditorState. While it's doing that we don't need to expand the stored CMS value.
    !environmentContext.get('isEditingThisInstance') &&
    !environmentContext.get('isEditingAnotherInstance');

  // Note: We use React.isValidElement because Search results pages can have `innerHTML` value that
  // is an array of React props that we must not confuse with JSON nodes.
  const isValueJsonNodes =
    Array.isArray(innerHTMLVal) && !React.isValidElement(innerHTMLVal[0]);

  // Note: richText CMS fields can either be stored as an html string (old) or jsonNode array (new).
  // We also want to expand plaintext fields into an element subtree when we need to edit them
  // with the ContentEditor (ie: in the EditorCanvas)
  const convertValueToElementTree =
    isValueJsonNodes || expandInnerHtmlForEditorContentEditor;

  if (convertValueToElementTree) {
    // TEMP (yoav): The ContentEditor needs rich text fields and plaintext fields to be expanded
    // into their element subtree equivalents for editing. At first we just want the Editor-canvas
    // and Data Managers to do this expansion, but we want the server to continue setting html dangerously
    // for the field values... in case there are differences we don't want to affect published sites

    // The next step will be to start doing the expansion on the server, and eventually run some kind of
    // CMS migration specifically for rich text fields in order to start saving them as jsonNodes.
    const plaintextMode = !isRichText(element);
    const expandedTextHolderElementChildren =
      getElementChildrenFromValue(plaintextMode)(innerHTMLVal)(components)(
        plugins
      );

    const newData = {
      ...(data || null),
      children: evalExpr(expandedTextHolderElementChildren)(
        freshEnvForReifiedSubtree(fullEnv)
      ),
    };

    return {
      ...props,
      innerHTML: undefined,
      data: newData,
    };
  }
  return props;
}

const innerHtmlBindingPath: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'bind'},
  {in: 'Record', at: 'innerHTML'},
];

function hasPlainTextOrRichTextInnerHtmlBinding(
  element: ExpressionElement
): boolean {
  const data = Expressions.getElementData(element);
  const bindingPath = Expressions.getIn(data, innerHtmlBindingPath);
  if (!bindingPath) return false;
  const fieldType = getBindingPathFieldType(bindingPath);
  return fieldType === 'PlainText' || fieldType === 'RichText';
}
